import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  Inject,
  inject,
  input,
  OnInit,
  output,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MapPoint } from '../data-access/models/map-point.model';
import { MapBounds } from '../data-access/models/map-bounds.model';
import { DpIconComponent } from '@client-monorepo/common/icon';
import { Control, Map, Marker } from 'leaflet';
import { DeviceDetector } from '@client-monorepo/common/utilities';
import { NgxHybridService, PermissionListEnum } from '@digipay/ngx-hybrid-service';
import { Coordination } from '@client-monorepo/common/location-management';

declare const window: any;
declare const L: any;

@Component({
  selector: 'lib-shared-common-map',
  standalone: true,
  imports: [CommonModule, DpIconComponent],
  templateUrl: './shared-common-map.component.html',
  styleUrl: './shared-common-map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedCommonMapComponent implements AfterViewInit, OnInit {
  points = input<MapPoint[]>([]);
  currentLocation = input<Coordination | null>(null);
  pointSelected = output<MapPoint>();
  areaChanged = output<MapBounds>();
  deviceDetector = inject(DeviceDetector);
  hybridService = inject(NgxHybridService);

  private map!: Map;
  isDesktop = this.deviceDetector.isDesktop();
  markerClusterGroup!: any;
  private markers: Marker[] = [];
  private currentLocationMarker?: Marker;
  private locationControl?: Control;
  locateMeIconRef = viewChild<ElementRef>('locateMeIcon');

  constructor(@Inject('APP_ENV') private environment: { [key: string]: string }) {
    effect(() => {
      const currentLocation = this.currentLocation();
      if (currentLocation && currentLocation.latitude && currentLocation.longitude) {
        this.map.setView([currentLocation.latitude, currentLocation.longitude], 15);
      }
    });

    effect(() => {
      if (this.map) {
        this.loadPoints();
      }
    });
  }

  ngOnInit() {
    if (this.hybridService.isAndroidHybrid()) {
      this.hybridService.getPermissions([PermissionListEnum.LOCATION]).then((result) => {
        const newresult: string[] = result.map((item) => String(item));
        if (newresult.includes(String(PermissionListEnum.LOCATION))) {
          this.hybridService.requestToEnableGps();
        }
      });
    }
  }

  ngAfterViewInit() {
    this.initMap();
    this.checkGeoPermissionAndRequestLocation();
  }

  mapMoveEnd(): void {
    this.emitAreaChanged();
  }

  private initMap(): void {
    this.map = L.map('map', {
      attributionControl: false,
      zoomControl: false,
      minZoom: 5,
      maxZoom: 18,
    }).setView([35.7599173, 51.4132244], 14);

    // Initialize marker cluster group
    this.markerClusterGroup = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: true,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (cluster: any) => {
        return L.divIcon({
          html: `<div style="background-color: #fff; border: 2px solid #2196f3; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;">${cluster.getChildCount()}</div>`,
          className: 'marker-cluster-custom',
          iconSize: L.point(32, 32),
        });
      },
    });
    this.map.addLayer(this.markerClusterGroup);
    this.addCustomLocationControl();
    L.tileLayer(`${this.environment['mapBaseUrl']}/{z}/{x}/{y}.png`, {
      attribution: '© MapTiler © OpenStreetMap contributors',
      maxZoom: 18,
      tileSize: 512,
      zoomOffset: -1,
    }).addTo(this.map);
    this.loadPoints();

    setTimeout(() => {
      this.emitAreaChanged();
    }, 0);
    this.map.on('moveend', () => {
      this.emitAreaChanged();
      this.mapMoveEnd();
    });
  }

  private addCustomLocationControl(): void {
    // Add custom location control
    this.locationControl = L.control.attribution({ position: 'topright' });
    if (this.locationControl) {
      this.locationControl.onAdd = () => {
        const div = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        div.style.border = 'none';
        const button = L.DomUtil.create('a', '', div);
        button.innerHTML = this.locateMeIconRef()?.nativeElement?.innerHTML || '📍';
        button.title = 'My Location';
        button.style.fontSize = '20px';
        button.style.width = '48px';
        button.style.height = '48px';
        button.style.textAlign = 'center';
        button.style.cursor = 'pointer';
        button.style.borderRadius = '100%';
        button.style.background = '#000436';
        button.style.color = 'white';
        button.style.padding = '12px';
        if (this.isDesktop) {
          button.style.marginRight = '345px';
          button.style.marginTop = '24px';
        }
        L.DomEvent.on(button, 'click', () => {
          this.moveToCurrentLocation();
        });
        return div;
      };
      this.locationControl.addTo(this.map);
    }
    this.moveToCurrentLocation();
  }

  emitAreaChanged(): void {
    const bounds = this.map.getBounds();
    const center = bounds.getCenter();
    const radius = center.distanceTo([bounds.getNorth(), bounds.getEast()]); // Calculate radius to corner

    this.areaChanged.emit({
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
      circle: {
        center: {
          lat: center.lat,
          lng: center.lng,
        },
        radius,
      },
    });
  }

  private checkGeoPermissionAndRequestLocation(): void {
    if (!navigator.permissions) {
      console.warn('Permissions API is not supported in this browser.');
      this.moveToCurrentLocation(); // Fallback to directly requesting
      return;
    }

    navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
      if (permissionStatus.state === 'granted') {
        this.moveToCurrentLocation();
      } else if (permissionStatus.state === 'prompt') {
        // User hasn't decided yet; we can still ask for permission
        this.moveToCurrentLocation();
      } else if (permissionStatus.state === 'denied') {
        console.warn('User denied location access. Please enable it in browser settings.');
        this.removeLocationMarker();
      }

      permissionStatus.onchange = () => {
        if (permissionStatus.state === 'granted') {
          if (!this.locationControl) {
            this.addCustomLocationControl();
          }
          this.moveToCurrentLocation();
        } else if (permissionStatus.state === 'denied') {
          this.removeLocationMarker();
        }
      };
    });
  }

  private moveToCurrentLocation(): void {
    // Request for GeoLocation API
    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        const { latitude, longitude } = position.coords;
        this.map.setView([latitude, longitude], 17);
        if (this.currentLocationMarker) {
          this.currentLocationMarker.setLatLng([latitude, longitude]);
        } else {
          const currentLocationIcon = L.icon({
            iconUrl: 'assets/my-location.png',
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          });
          this.currentLocationMarker = L.marker([latitude, longitude], {
            icon: currentLocationIcon,
            zIndexOffset: 900000,
          }).addTo(this.map);
        }
      },
      (error: GeolocationPositionError) => {
        console.error('Error getting location:', error.message);
        if (error?.code === error.PERMISSION_DENIED) {
          console.warn('User denied geolocation. Removing location control.');
          this.removeLocationMarker();
        }
      },
    );
  }

  private removeLocationMarker(): void {
    if (this.locationControl) {
      this.map.removeControl(this.locationControl);
      this.locationControl = undefined;
    }
    if (this.currentLocationMarker) {
      this.map.removeLayer(this.currentLocationMarker);
      this.currentLocationMarker = undefined;
    }
  }

  private loadPoints(): void {
    // Clear existing markers
    this.markerClusterGroup.clearLayers();
    this.markers = [];

    // Add new markers
    this.points().forEach((point) => {
      const marker = L.marker([point.lat, point.lng]);
      if (point.html) {
        const customIcon = L.divIcon({
          html: point.html,
          className: '',
          iconSize: [32, 35],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
        });
        marker.setIcon(customIcon);
      }
      marker.on('click', () => {
        this.pointSelected.emit(point);
      });
      this.markerClusterGroup.addLayer(marker);
      this.markers.push(marker);
    });
  }
}
